import { useGetIndustriesLazyQuery } from "@graphql/generated";
import { motion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import SearchableSelector from "../SearchableSelector";
import industryIcons from "./industry-icons";

export type Industry = {
  value: string;
  label: string;
};

type IndustrySelectorProps = {
  selectedIndustry?: string;
  onSelect: (value: string) => void;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
};

const IndustrySelector = ({ selectedIndustry, onSelect, scrollContainerRef }: IndustrySelectorProps) => {
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [hasMoreIndustries, setHasMoreIndustries] = useState(true);
  const isLoadingMore = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const [fetchIndustries, { loading, data }] = useGetIndustriesLazyQuery({
    variables: {
      first: 20,
      name: searchTerm,
    },
    fetchPolicy: "cache-and-network",
  });

  const loadMoreIndustries = useCallback(() => {
    if (data?.common?.industries?.pageInfo.endCursor && !isLoadingMore.current) {
      isLoadingMore.current = true;
      fetchIndustries({
        variables: {
          first: 20,
          name: searchTerm,
          after: data.common.industries.pageInfo.endCursor,
        },
      });
    }
  }, [data, fetchIndustries, searchTerm]);

  useEffect(() => {
    fetchIndustries();
  }, [fetchIndustries]);

  useEffect(() => {
    const handleScroll = () => {
      if (!hasMoreIndustries || loading) return;

      const scrollContainer = scrollContainerRef?.current || containerRef.current;
      if (!scrollContainer) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        loadMoreIndustries();
      }
    };

    const scrollContainer = scrollContainerRef?.current || containerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, [hasMoreIndustries, loading, loadMoreIndustries, scrollContainerRef]);

  useEffect(() => {
    if (data?.common?.industries?.edges) {
      const newIndustries = data.common.industries.edges.map((edge) => ({
        value: edge?.node?.id ?? "",
        label: edge?.node?.title ?? "",
      }));

      if (isLoadingMore.current) {
        setIndustries((prevIndustries) => {
          const existingIds = new Set(prevIndustries.map((i) => i.value));
          const uniqueNewIndustries = newIndustries.filter((i) => !existingIds.has(i.value));
          return [...prevIndustries, ...uniqueNewIndustries];
        });
      } else {
        setIndustries(newIndustries);
      }

      setHasMoreIndustries(!!data.common.industries.pageInfo.hasNextPage);
      isLoadingMore.current = false;
    }
  }, [data]);

  const handleSearch = (search: string) => {
    setSearchTerm(search);
    isLoadingMore.current = false;
    setIndustries([]);
    fetchIndustries({
      variables: {
        first: 20,
        name: search,
        after: "",
      },
    });
  };

  const getIndustryIcon = (value: string) => {
    const IconComponent = industryIcons[value];

    if (IconComponent) {
      return (
        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
          <IconComponent className="w-5 h-5 text-primary-600 fill-primary-600" />
        </div>
      );
    }

    return (
      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
        <span className="text-primary-600 text-sm">
          {industries.find((i) => i.value === value)?.label.charAt(0) ?? "?"}
        </span>
      </div>
    );
  };

  const renderIndustryItem = (industry: Industry) => (
    <div className="flex flex-col justify-center items-center gap-2">
      <div className="w-10 h-10 mb-2 flex items-center justify-center">{getIndustryIcon(industry.value)}</div>
      <p className="text-xs font-medium text-center">{industry.label}</p>
    </div>
  );

  return (
    <div ref={containerRef}>
      <motion.h2
        className="text-xl lg:text-3xl font-bold mb-4 md:mb-10"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        Which industry do you work in?
      </motion.h2>
      <div className="w-full mx-auto">
        <SearchableSelector
          options={industries}
          selectedValue={selectedIndustry}
          onSelect={onSelect}
          placeholder="Search for an industry..."
          loading={loading}
          onSearch={handleSearch}
          hasMore={hasMoreIndustries}
          virtualized={true}
          renderOption={renderIndustryItem}
          gridLayout={true}
          containerScrolling={true}
        />
      </div>
    </div>
  );
};

export default IndustrySelector;
